import { io, Socket } from "socket.io-client";
import { AgentAIConfig, ConnectionState } from "../types";
import { clientBus, internalBus } from "../utils/eventBus";
import { AgentAIApi } from "./api";
import {
  CLIENT_EVENTS,
  INTERNAL_EVENTS,
  SOCKET_EVENTS,
} from "../constants/eventConsts";
import KoreGenerateuuidPipe from "../utils/uuid";

export class websocketService {
  connectionState: ConnectionState = "disconnected";
  _reconnectAttempts: number = 0;
  protected socket: Socket | null = null;
  config: AgentAIConfig = {} as AgentAIConfig;

  socketConnection(api: AgentAIApi, nameSpacePath: string) {
    this.socket = null; // Will be set when socket is created
    const { conversationId, botId, channel, interactiveLanguage } =
      this.config.connection;
    const { domainURL } = this.config.connection;
    let queryParams: any = {
      accountId: api.getAccountId(),
      botId,
      experience: channel,
      conversationId,
      userId: api.getUserId(),
      language: interactiveLanguage,
      interactiveLang: interactiveLanguage,
      accessToken: api.getAuthToken(),
      cE: true,
    };

    let finalUrl = `${domainURL}/${nameSpacePath}`;
    const subPath = api.getService();
    const socketConfig = {
      url: finalUrl,
      options: {
        path: `/${subPath}/api/v1/chat`,
        autoConnect: false,
        transports: ["websocket", "polling", "flashsocket"],
        reconnection: true,
        reconnectionDelay: 5000,
        reconnectionAttempts: 13,
        query: queryParams,
      },
    };

    this.socket = io(socketConfig.url, socketConfig.options);
    this.socket.connect();
    this.setupEventListeners();
  }

  setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on(SOCKET_EVENTS.CONNECT, () => {
      this.connectionState = "connected";
      this._reconnectAttempts = 0;
      clientBus.emitBusEvent(CLIENT_EVENTS.CONNECT, {
        content: "connected",
        metadata: { status: "connected" },
      });
    });

    this.socket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
      this.connectionState = "disconnected";
      clientBus.emitBusEvent(CLIENT_EVENTS.DISCONNECT, {
        content: reason,
        metadata: { reason },
      });
    });

    this.socket.on(SOCKET_EVENTS.CONNECT_ERROR, (error) => {
      clientBus.emitBusEvent(CLIENT_EVENTS.CONNECT_ERROR, {
        content: error.message,
        metadata: { name: error.name },
      });
    });

    this.socket.io.on("reconnect", (attempt) => {
      this.connectionState = "connected";
      this._reconnectAttempts = 0;
      clientBus.emitBusEvent(CLIENT_EVENTS.RECONNECT, {
        content: "reconnected",
        metadata: { attempt },
      });
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      this.connectionState = "reconnecting";
      this._reconnectAttempts = attempt;
      clientBus.emitBusEvent(CLIENT_EVENTS.RECONNECT_ATTEMPT, {
        content: "reconnecting",
        metadata: { attempt },
      });
    });

    this.socket.on(SOCKET_EVENTS.AA_READY, (event: any) => {
      clientBus.emitBusEvent(CLIENT_EVENTS.AGENTAI_READY, { ready: true });
      internalBus.emit(INTERNAL_EVENTS.AGENTAI_READY, event);
    });

    this.socket.on(SOCKET_EVENTS.USER_SAVED_MESSAGE, (event: any) => {
      clientBus.emitBusEvent(CLIENT_EVENTS.USER_SAVED_MESSAGE, event);
    });

    this.socket.on(SOCKET_EVENTS.AGENT_SAVED_MESSAGE, (event: any) => {
      clientBus.emitBusEvent(CLIENT_EVENTS.AGENT_SAVED_MESSAGE, event);
    });

    this.socket.on(SOCKET_EVENTS.INTERNAL_TRANSFER_RESPONSE, (event: any) => {
      internalBus.emit(INTERNAL_EVENTS.INTERNAL_TRANSFER_RESPONSE, event);
      clientBus.emitBusEvent(CLIENT_EVENTS.INTERNAL_TRANSFER_RESPONSE, event);
    });

    this.socket.on(SOCKET_EVENTS.RESPONSE_RESOLUTION_COMMENTS, (event: any) => {
      internalBus.emit(INTERNAL_EVENTS.RESPONSE_RESOLUTION_COMMENTS, event);
      clientBus.emitBusEvent(CLIENT_EVENTS.INTERM_SUMMARY_RESPONSE, event);
    });

    this.socket.on(SOCKET_EVENTS.CONV_META_INFO, (event: any) => {
      internalBus.emit(INTERNAL_EVENTS.CONV_META_INFO, event);
    });

    this.socket.on(SOCKET_EVENTS.AGENT_ASSIST_RESPONSE, (event: any) => {
      if (
        !(event?.eventName === "service_execution_in_progress") &&
        !event?.sendMenuRequest
      ) {
        internalBus.emit(INTERNAL_EVENTS.WELCOME_MESSAGE_SENT, false);
      }
    });

    this.socket.on(SOCKET_EVENTS.AGENTAI_EVENT_ACK, (event: any) => {
      if (event?.expEvent === "response_resolution_comments") {
        clientBus.emitBusEvent(CLIENT_EVENTS.INTERM_SUMMARY_EVENT_ACK, event);
      }
    });
  }

  emitSocketEvent(event: string, payload: unknown): void {
    if (!this.socket?.connected) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return;
    }
    if (!this.config) {
      clientBus.emitError("Configuration is not set.");
      return;
    }
    const data: any = {
      isExtAD: true,
      experience: this.config.connection.channel,
      traceId: KoreGenerateuuidPipe(),
      conversationId: this.config.connection.conversationId,
      botId: this.config.connection.botId,
      language: this.config.connection.interactiveLanguage,
      participant: (payload as any)?.participant || {},
    };
    Object.assign(data, payload);
    this.socket.emit(event, data);
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionState = "disconnected";
    }
  }
}
